/**
 * @author Fernando Ruano
 * @file index.jsx
 * @description Roles Edit/View Page.
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppCardLayout from '@nephro/components/AppCardLayout';

// Components
import FormGenerator from '@nephro/components/FormGenerator';

// Helpers
import {
  getLastItemUrl,
  bottomOptionsCreateEdit,
  bottomOptionsRead,
  formItemNameDesc
} from '@nephro/commons/helpers/utils';

// Styles
import styles from './styles.module.css';

let roleData = {};

/**
 * Roles View/Edit
 * @param { Object } roleActions - Role Actions.
 * @param { Object } actions - App Actions.
 * @return { Node } React node.
 */
const RolesViewEdit = ({ roleActions, actions }) => {
  const history = useHistory();
  const location = useLocation();
  const { t: translate } = useTranslation();

  const isView = location.pathname.includes('view');

  const [isReadOnly] = React.useState(isView);
  const [roleInfo, setRoleInfo] = React.useState(null);
  const [roleId] = React.useState(
    parseInt(getLastItemUrl(location.pathname), 10)
  );

  const { getRoleById, updateRole } = roleActions;
  const { setLoading } = actions;

  const onGoBack = () => {
    history.goBack();
  };

  // Function to get and set role info
  const onGetRoleInfo = (_, response) => {
    if (response.data) {
      const { description, name } = response.data;
      roleData = { description, name };
      setRoleInfo({ description, name }, setLoading(false));
    } else {
      onGoBack();
    }
  };

  React.useEffect(() => {
    getRoleById({ id: roleId }, onGetRoleInfo);
  }, []);

  // Function to get on submit form values
  const onFinish = (values) => {
    updateRole({ ...values, id: roleId }, onGoBack);
  };

  return (
    <AppCardLayout>
      <div className="flex flex-col h-full px-7 pt-5 pb-7 ">
        {roleInfo && (
          <FormGenerator
            initialValues={roleData}
            onFinish={onFinish}
            items={formItemNameDesc(translate, isReadOnly)}
            bottomOptions={
              !isReadOnly
                ? bottomOptionsCreateEdit(
                    onGoBack,
                    translate,
                    'edit',
                    styles.actionButton
                  )
                : bottomOptionsRead(onGoBack, translate, styles.actionButton)
            }
          />
        )}
      </div>
    </AppCardLayout>
  );
};

/**
 * Component definition of proptypes.
 */
RolesViewEdit.propTypes = {
  actions: PropTypes.shape({
    setLoading: PropTypes.func.isRequired
  }).isRequired,
  roleActions: PropTypes.shape({
    createRole: PropTypes.func.isRequired,
    getRoleById: PropTypes.func.isRequired,
    updateRole: PropTypes.func.isRequired
  }).isRequired
};

export default RolesViewEdit;
